/**
 * AuthContext
 * Provides authentication state and user information throughout the app
 * Integrates with existing authService
 */

import * as React from 'react';
import type { User } from '@/types/auth.types';
import { getCurrentUser } from '@/services/authService';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
}

/**
 * Raw auth context. Exported so non-production consumers (Storybook, tests
 * that need to wrap children directly) can provide their own value without
 * instantiating the full `AuthProvider`. Production code should continue to
 * consume via `useAuth`.
 */
export const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

/**
 * Hook to use auth context
 * Provides access to current user and auth state
 */
export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider Component
 * Manages authentication state and provides it to children
 * Integrates with existing authService (mock or real backend)
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const refreshUser = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize auth state on mount
  React.useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const value = React.useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      refreshUser,
    }),
    [user, isLoading, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
